import { useCallback, useEffect, useRef, useState } from "react";

import { useForm, UseFormReturn } from "react-hook-form";

import { UseMutationResult } from "@tanstack/react-query";

import useDefaultCRUDHandlers from "../../hooks/useDefaultCRUDHandlers";
import { ButtonInput } from "../shared/Inputs/ButtonInput";
import { NumberInput } from "../shared/Inputs/NumberInput";
import { TextInput } from "../shared/Inputs/TextInput";
import { IPartCategory, PartCategoryPosition, PartCategorySide } from "../../models/Category/Category";
import Form from "../base/Form/Form";
import { FormTextField } from "../base/Form/FormTextField";
import { FormNumberField } from "../base/Form/FormNumberField";
import { FormSelect } from "../base/Form/FormSelect";
import { Button } from "../base/Button";
import { useNavigate } from "react-router-dom";
import CategoryDiagramModal from "./CategoryDiagramModal";


function parseProfiler(value: number | string | null | undefined): number | null {
  if (value === "" || value === null || value === undefined) return null;
  const parsed = typeof value === "number" ? value : Number(value);
  return Number.isFinite(parsed) ? parsed : null;
}

type CategoryFormValues = {
  name: string,
  partOnly: boolean,
  mechOnly: boolean,
  postage: number | null | undefined,
  whCost: number | null | undefined,
  whTime: number | null | undefined,
  disCost: number | null | undefined,
  disTime: number | null | undefined,
  pinnacleName: string,
  rsName: string,
  frontierName: string,
  tractableName: string,
  shopifyTaxonomyId: string,
  googleProductCategoryId: string,
  ucid: string,
  ucidZone: string,
  skuPrefix: string,
  bodyProfiler: number | null | undefined,
  mechanicalProfiler: number | null | undefined,
  side: number,
  position: number,
  assemblyNames: string,
  requireSecuirtySeal: boolean,
  excludeFromPartProTrade: boolean,
};

interface CategoryDetailsProps {
  category: IPartCategory | undefined,
  update: UseMutationResult<IPartCategory, unknown, IPartCategory, unknown>,
  create: UseMutationResult<IPartCategory, unknown, IPartCategory, unknown>,
  open?: boolean,
  setOpen?: React.Dispatch<React.SetStateAction<boolean>>,
  /** Pre-fills the name of a new category. */
  defaultName?: string,
  /**
   * When set, creating stays on the current page and hands back the new category instead of going
   * to the category list. Used where a category is created in the middle of something else, such
   * as the Pinnacle mapping page.
   */
  onCreated?: (category: IPartCategory) => void,
};


export default function CategoryDetails({ category, update, create, open, setOpen, defaultName, onCreated }: CategoryDetailsProps) {
  const navigate = useNavigate();
  const { register, handleSubmit, reset, watch } = useForm<CategoryFormValues>();
  const { saveHandlers } = useDefaultCRUDHandlers("Category");
  const [reqImageEditOpen, setReqImageEditOpen] = useState(false);
  const [diagramOpen, setDiagramOpen] = useState(false);
  const formMethodsRef = useRef<UseFormReturn<CategoryFormValues> | null>(null);

  const handleFormReady = useCallback((methods: UseFormReturn<CategoryFormValues>) => {
    formMethodsRef.current = methods;
  }, []);

  const handleDiagramSave = useCallback((zone: string, partCode: string | null) => {
    const methods = formMethodsRef.current;
    if (!methods) return;

    methods.setValue("ucidZone", zone);

    if (partCode) {
      const currentUcid = methods.getValues("ucid") || "";
      const existing = currentUcid.split(",").map((s: string) => s.trim()).filter(Boolean);
      if (!existing.includes(partCode)) {
        methods.setValue("ucid", existing.length ? `${existing.join(", ")}, ${partCode}` : partCode);
      }
    }
  }, []);

  const partSides = [...Object.values(PartCategorySide).filter((ct) => isNaN(Number(ct)))];
  const partPositions = [...Object.values(PartCategoryPosition).filter((ct) => isNaN(Number(ct)))];

  const onSubmit = (data: any) => {

    const updatedCategory = {
      ...category,
      ...data,
      side: parseInt(data.side),
      position: parseInt(data.position),
      bodyProfiler: parseProfiler(data.bodyProfiler),
      mechanicalProfiler: parseProfiler(data.mechanicalProfiler),
    };

    if (category !== undefined) {
      update.mutate(updatedCategory, saveHandlers);
    } else if (onCreated) {
      // Stays open until the save succeeds, so a rejected name (it must be unique) can be fixed.
      create.mutate(updatedCategory, {
        onSuccess: (res: any) => {
          saveHandlers.onSuccess();
          reset({});
          setOpen && setOpen(false);
          onCreated(res?.data ?? res);
        },
        onError: saveHandlers.onError,
      });
      return;
    } else {
      create.mutate(updatedCategory, saveHandlers);
      navigate("/categories");
      reset({});
    }

    setOpen && setOpen(false);
  };

  useEffect(() => {
    if (category !== undefined) {
      reset({
        name: category.name,
        partOnly: category.partOnly,
        mechOnly: category.mechOnly,
        postage: category.postage,
        whCost: category.whCost,
        whTime: category.whTime,
        disCost: category.disCost,
        disTime: category.disTime,
        pinnacleName: category.pinnacleName,
        frontierName: category.frontierName,
        tractableName: category.tractableName,
        shopifyTaxonomyId: category.shopifyTaxonomyId,
        googleProductCategoryId: category.googleProductCategoryId,
        ucid: category.ucid,
        ucidZone: category.ucidZone,
        skuPrefix: category.skuPrefix,
        bodyProfiler: category.bodyProfiler,
        mechanicalProfiler: category.mechanicalProfiler,
        side: category.side,
        position: category.position,
        excludeFromPartProTrade: category.excludeFromPartProTrade,
        // assemblyNames: category.partAssemblyPartCategories?.map(x => x.partAssembly?.name).join(", "),
        // requireSecuirtySeal: imageRequirements?.data?.partRequiredImages.some(x => x.type == 8)
      });
    } else {
      reset(defaultName ? { name: defaultName } : {})
    }
  }, [category, open, reset, defaultName]);


  return (
  <>
    <Form<CategoryFormValues>
      onSubmit={onSubmit}
      defaultValues={{
        ...(category ?? (defaultName ? { name: defaultName } : {})),
      }}
      onReady={handleFormReady}
    >
      {({ register }: UseFormReturn<CategoryFormValues>) => (
        <div className="space-y-4 border-t border-gray-200">
          <div className="pt-4 grid grid-cols-1 gap-x-4 gap-y-4 sm:gap-y-6 sm:grid-cols-2">

            <FormTextField label="Name" {...register("name")} />
            <FormNumberField label="Default Postage (£)" {...register("postage")} step=".01" />

            <FormNumberField label="Default Warehouse Time" {...register("whTime")} />
            <FormNumberField label="Default Warehouse Cost  (£)" {...register("whCost")} step=".01" />

            <FormNumberField label="Default Disassembly Time" {...register("disTime")} />
            <FormNumberField label="Default Disassembly Cost (£)" {...register("disCost")} step=".01" />

            <FormSelect label="Side" {...register("side")}>
              {partSides.map((ct, i) => (
                <option key={ct} value={i}>
                  {ct}
                </option>
              ))}
            </FormSelect>
            <FormSelect label="Position" {...register("position")}>
              {partPositions.map((ct, i) => (
                <option key={ct} value={i}>
                  {ct}
                </option>
              ))}
            </FormSelect>

            <FormTextField label="Pinnacle Name" {...register("pinnacleName")} />
            <FormTextField label="Rs Automotive Name" {...register("rsName")} />
            <FormTextField label="Frontier Name" {...register("frontierName")} />
            <FormTextField label="Tractable Category Name" {...register("tractableName")} />

            {/*
              Shopify's Motor Vehicle Parts taxonomy (vp-1-4-*). This is what files the part on
              PartPro — PartPro's catalogue is the same taxonomy, so the id maps straight across —
              and it is the Shopify product category too. Left blank, PartPro guesses from the title.
            */}
            <FormTextField
              label="Shopify Taxonomy ID"
              placeholder="e.g. vp-1-4-1-1"
              {...register("shopifyTaxonomyId")}
            />
            <FormTextField
              label="Google Product Category"
              placeholder="e.g. 2977"
              {...register("googleProductCategoryId")}
            />

            {/*
              Scoring weights. Admin-owned: synced onto every customer category and
              kept off the customer category editor.
            */}
            <FormNumberField label="Body Profiler" {...register("bodyProfiler")} step=".01" />
            <FormNumberField label="Mechanical Profiler" {...register("mechanicalProfiler")} step=".01" />

            <FormTextField label="SKU Prefix" {...register("skuPrefix")} />
            {/* <FormTextField label="Assemblies" {...register("assemblyNames")} disabled={true} /> */}
            <FormTextField label="UCID" {...register("ucid")} />
            <div className="flex items-end gap-2">
              <div className="flex-1">
                <FormTextField label="UCID Zone" {...register("ucidZone")} />
              </div>
              <Button type="button" onClick={() => setDiagramOpen(true)}>
                Find on Diagram
              </Button>
            </div>


            <div className="flex flex-wrap items-center justify-between">
              <div>
                <input
                  id="bodyPart"
                  type="checkbox"
                  {...register("partOnly")}
                />
                <label htmlFor="bodyPart" className="ml-3 text-sm font-medium text-gray-700">Body Part?</label>
              </div>
              <div>
                <input
                  id="mechPart"
                  type="checkbox"
                  {...register("mechOnly")}
                />
                <label htmlFor="mechPart" className="ml-3 text-sm font-medium text-gray-700">Mechanical Part?</label>
              </div>
              <div>
                <input
                  id="requireSecuirtySeal"
                  type="checkbox"
                  {...register("requireSecuirtySeal")}
                />
                <label htmlFor="requireSecuirtySeal" className="ml-3 text-sm font-medium text-gray-700">Require Security Seal?</label>
              </div>
            </div>

            <div>
              <input
                id="excludeFromPartProTrade"
                type="checkbox"
                {...register("excludeFromPartProTrade")}
              />
              <label htmlFor="excludeFromPartProTrade" className="ml-3 text-sm font-medium text-gray-700">Don't sync to PartPro trade</label>
              <p className="ml-7 text-xs text-gray-500">Hidden as a bid category on PartPro trade from the next nightly sync (01:30).</p>
            </div>

            <div>
              {/* spacer */}
            </div>

            <div className="flex gap-x-4">
              <Button type="button" onClick={() => {setOpen && setOpen(false);}}>
                Cancel
              </Button>
              <Button type="submit">
                Save
              </Button>
            </div>

          </div>
        </div>
      )}
    </Form>

    <CategoryDiagramModal
      open={diagramOpen}
      setOpen={setDiagramOpen}
      ucid={category?.ucid ?? ""}
      ucidZone={category?.ucidZone ?? ""}
      onSave={handleDiagramSave}
    />
  </>
  );
}